import React, { useState, useMemo } from 'react'
import { Icon } from '@/design-system/atoms/Icon'
import { useMessageFormatting } from '../../../hooks/useMessageFormatting'
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard'
import { MarkdownRenderer } from './MarkdownRenderer'
import { PlainTextRenderer } from './PlainTextRenderer'
import { MessagePreview } from './MessagePreview'
import { MessageActions } from './MessageActions'
import { formatMessageTime } from '../../../constants/processingMessages'
import { OptimusAvatar } from '../OptimusAvatar'
import { UserAvatar } from '../UserAvatar'
import { DataModalTrigger } from '../DataModalTrigger'
import { InlineDataTableTabs } from '../InlineDataTable'
import { parseStructuredData } from '../../../types/structuredData'
import { parseTableData } from '../../../types/tableData'

export interface MessageSender {
  id: number
  name: string
  username: string
}

export interface SystemMessageMetadata {
  action?: string
  actorId?: number
  actorName?: string
  targetName?: string
  targetType?: 'user' | 'group'
}

export interface MessageData {
  id: string
  type: 'user' | 'agent' | 'system'
  content: string
  timestamp: string
  sender?: MessageSender
  metadata?: SystemMessageMetadata
}

export interface MessageRendererProps {
  message: MessageData
  userName?: string
  variant?: 'full' | 'preview' | 'history'
  showActions?: boolean
  showMetadata?: boolean
  onEdit?: () => void
  onCopy?: (content: string) => void
  onRegenerate?: () => void
  className?: string
}

/**
 * Base message renderer with variant support
 *
 * Variants:
 * - full: Complete message with all features (default)
 * - preview: Compact preview for cards/notifications
 * - history: Simplified view for history sidebar
 *
 * Features:
 * - Automatic markdown detection and rendering
 * - Copy to clipboard with fallback
 * - Toggle between raw and rendered view (agent messages only)
 * - Role-based styling (user vs agent)
 * - Configurable actions per variant
 */
export const MessageRenderer: React.FC<MessageRendererProps> = ({
  message,
  userName,
  variant = 'full',
  showActions = true,
  showMetadata = true,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onEdit: _onEdit, // Reserved for future use
  onCopy,
  onRegenerate,
  className = '',
}) => {
  const [showRaw, setShowRaw] = useState(false)

  // Parse structured data from message content (only for agent messages)
  const parsedContent = useMemo(() => {
    if (message.type === 'agent') {
      return parseStructuredData(message.content)
    }
    return null
  }, [message.content, message.type])

  // Parse table data from message content (only for agent messages)
  const tableContent = useMemo(() => {
    if (message.type === 'agent') {
      return parseTableData(message.content)
    }
    return null
  }, [message.content, message.type])

  // Use text content without structured/table data tags for formatting
  const contentForFormatting =
    tableContent?.textContent || parsedContent?.textContent || message.content
  const { formattedContent, isMarkdown } =
    useMessageFormatting(contentForFormatting)
  const { copy, copied } = useCopyToClipboard()

  const handleCopy = () => {
    if (onCopy) {
      onCopy(message.content)
    } else {
      copy(message.content)
    }
  }

  const toggleView = () => {
    setShowRaw(!showRaw)
  }

  // Preview variant - truncated compact view
  if (variant === 'preview') {
    return (
      <div
        className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} ${className}`}
      >
        <div
          className={`max-w-[70%] px-3 py-2 rounded-lg ${
            message.type === 'user'
              ? 'bg-brand-600 text-white'
              : 'bg-surface-tertiary text-content-primary'
          }`}
        >
          <MessagePreview
            content={formattedContent}
            maxLength={100}
            className="text-xs"
          />
        </div>
      </div>
    )
  }

  // History variant - simplified view without actions
  if (variant === 'history') {
    return (
      <div
        className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'} ${className}`}
      >
        <div
          className={`max-w-[80%] px-3 py-2 rounded-lg ${
            message.type === 'user'
              ? 'bg-brand-600 text-white'
              : 'bg-surface-tertiary text-content-primary'
          }`}
        >
          {isMarkdown ? (
            <MarkdownRenderer content={formattedContent} className="text-xs" />
          ) : (
            <PlainTextRenderer content={formattedContent} className="text-xs" />
          )}
        </div>
      </div>
    )
  }

  // Full variant - complete message with all features

  // System messages - centered with distinct styling
  if (message.type === 'system') {
    return (
      <div className={`flex justify-center my-4 ${className}`}>
        <div className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-full text-xs text-gray-600 dark:text-gray-400">
          <Icon name="info" size={16} />
          <span>{message.content}</span>
          <span className="text-gray-400 dark:text-gray-500">
            {formatMessageTime(message.timestamp)}
          </span>
        </div>
      </div>
    )
  }

  // User messages - display with avatar on right
  if (message.type === 'user') {
    // Determine display name: use sender name from metadata if available, otherwise userName prop
    const displayName = message.sender?.name || userName || 'U'
    const showSenderName =
      message.sender?.name && message.sender.name !== userName

    return (
      <div className={`group flex justify-end ${className}`}>
        <div className="flex items-start gap-3 max-w-[85%]">
          {/* Message content */}
          <div className="flex-1 min-w-0">
            {/* Show sender name if different from current user (shared session) */}
            {showSenderName && (
              <div className="text-right text-xs text-gray-500 dark:text-gray-400 mb-1 mr-1">
                {message.sender?.name}
              </div>
            )}
            <div className="bg-brand-600 text-white px-4 py-3 rounded-2xl rounded-tr-sm shadow-sm">
              <PlainTextRenderer content={formattedContent} />
            </div>
            {/* Timestamp - outside the bubble for readability */}
            <div className="flex justify-end mt-1 mr-1">
              <span className="text-[10px] text-content-tertiary tabular-nums">
                {formatMessageTime(message.timestamp)}
              </span>
            </div>
          </div>
          {/* User Avatar */}
          <UserAvatar name={displayName} size="md" />
        </div>
      </div>
    )
  }

  // Agent messages - full featured display
  const hasTable = tableContent?.data && !showRaw

  return (
    <div
      className={`group ${hasTable ? 'max-w-full' : 'max-w-[85%]'} ${className}`}
    >
      {/* Optimus Avatar + Message */}
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <OptimusAvatar size="md" />

        {/* Message content */}
        <div className="flex-1 min-w-0">
          <div className="bg-surface-tertiary px-4 py-3 rounded-2xl rounded-tl-sm border border-gray-100 dark:border-gray-800">
            <div className="text-content-primary text-sm font-normal leading-relaxed">
              {showRaw ? (
                <pre className="whitespace-pre-wrap font-mono text-xs bg-surface-secondary text-content-primary p-3 rounded-lg overflow-x-auto border border-gray-200 dark:border-gray-700">
                  {message.content}
                </pre>
              ) : isMarkdown ? (
                <MarkdownRenderer content={formattedContent} />
              ) : (
                <PlainTextRenderer content={formattedContent} />
              )}

              {/* Structured data trigger - rendered below text content */}
              {parsedContent?.data && !showRaw && (
                <div className="mt-4">
                  <DataModalTrigger data={parsedContent.data} />
                </div>
              )}
            </div>
          </div>
          {/* Timestamp - outside the bubble for readability */}
          {!hasTable && (
            <div className="flex justify-start mt-1 ml-1">
              <span className="text-[10px] text-content-tertiary tabular-nums">
                {formatMessageTime(message.timestamp)}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Table data - rendered OUTSIDE the bubble, full width */}
      {hasTable && (
        <div className="mt-3 ml-11">
          <InlineDataTableTabs data={tableContent.data} />
          <div className="flex justify-end mt-1">
            <span className="text-[10px] text-gray-500 dark:text-gray-400">
              {formatMessageTime(message.timestamp)}
            </span>
          </div>
        </div>
      )}

      {/* Actions - aligned with message content (after avatar) */}
      {showActions && variant === 'full' && (
        <div className="ml-11">
          <MessageActions
            onCopy={handleCopy}
            onRegenerate={onRegenerate}
            onToggleRaw={toggleView}
            copied={copied}
            showRaw={showRaw}
            className="mt-2"
          />
        </div>
      )}

      {/* Metadata placeholder for future expansion */}
      {showMetadata && variant === 'full' && (
        <div className="mt-2 ml-11 text-xs text-gray-400">
          {/* Future: token usage, model info, etc */}
        </div>
      )}
    </div>
  )
}
